import { useId, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react';
import { cx, sizeClass, type Hue, type Size } from './shared';

// ------------------------------------------------------------------- field

export interface FieldProps {
  label: ReactNode;
  /** The control's id; defaults to a generated one passed to the control via render prop. */
  htmlFor?: string;
  hint?: ReactNode;
  /** Shown instead of the hint when set. */
  error?: ReactNode;
  required?: boolean;
  /** Label and hint on the left, control on the right (settings rows). */
  row?: boolean;
  className?: string;
  children: ReactNode;
}

/** Label + control + hint or error. Pass the control's id as htmlFor. */
export function Field({ label, htmlFor, hint, error, required, row, className, children }: FieldProps) {
  const text = (
    <>
      <label className="field__label" htmlFor={htmlFor}>
        {label}
        {required && (
          <span className="field__required" aria-hidden="true">
            *
          </span>
        )}
      </label>
      {error ? <span className="field__error">{error}</span> : hint && <span className="field__hint">{hint}</span>}
    </>
  );

  return (
    <div className={cx('field', row && 'field--row', className)}>
      {row ? <div className="field__text">{text}</div> : text}
      {children}
    </div>
  );
}

// ----------------------------------------------------------- text controls

interface ControlOptions {
  size?: Size;
  invalid?: boolean;
}

export interface InputProps extends ControlOptions, Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  /** Inert but legible (e.g. a value that can't change here). */
  muted?: boolean;
}

export function Input({ size, invalid, muted, className, ...rest }: InputProps) {
  return (
    <input
      className={cx('input', sizeClass('input', size), muted && 'input--muted', className)}
      aria-invalid={invalid || undefined}
      {...rest}
    />
  );
}

export interface TextareaProps extends ControlOptions, TextareaHTMLAttributes<HTMLTextAreaElement> {}

export function Textarea({ size, invalid, className, ...rest }: TextareaProps) {
  return (
    <textarea className={cx('input', sizeClass('input', size), className)} aria-invalid={invalid || undefined} {...rest} />
  );
}

export interface SelectProps extends ControlOptions, Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> {}

export function Select({ size, invalid, className, ...rest }: SelectProps) {
  return (
    <select className={cx('select', sizeClass('select', size), className)} aria-invalid={invalid || undefined} {...rest} />
  );
}

/** A text input with an icon or text inside its start (or end). */
export function InputGroup({ start, end, children }: { start?: ReactNode; end?: ReactNode; children: ReactNode }) {
  return (
    <div className="input-group">
      {start && <span className="input-group__addon">{start}</span>}
      {children}
      {end && <span className="input-group__addon input-group__addon--end">{end}</span>}
    </div>
  );
}

export interface SearchInputProps extends Omit<InputProps, 'type' | 'onChange' | 'value'> {
  value: string;
  onChange: (value: string) => void;
  /** Label for the clear button. Default "Clear search". */
  clearLabel?: string;
}

const searchIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
    <circle cx="11" cy="11" r="7" />
    <path d="m20 20-3.5-3.5" />
  </svg>
);

/** A search box: an InputGroup with a search icon, and a clear (×) button once there is text. */
export function SearchInput({ value, onChange, clearLabel = 'Clear search', ...rest }: SearchInputProps) {
  return (
    <InputGroup
      start={searchIcon}
      end={
        value ? (
          <button type="button" className="input-group__button" aria-label={clearLabel} onClick={() => onChange('')}>
            ×
          </button>
        ) : undefined
      }
    >
      <Input type="search" value={value} onChange={(e) => onChange(e.target.value)} {...rest} />
    </InputGroup>
  );
}

// ----------------------------------------------------------- choice inputs

export interface ChoiceProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'type'> {
  hue?: Hue;
  size?: 'sm' | 'base' | 'lg';
  invalid?: boolean;
  /** Wraps the input in a .check label with this text. */
  label?: ReactNode;
  hint?: ReactNode;
  /** Set the indeterminate state (checkbox only). */
  indeterminate?: boolean;
}

function choice(block: 'checkbox' | 'radio' | 'switch', type: 'checkbox' | 'radio') {
  const Choice = ({ hue, size, invalid, label, hint, indeterminate, className, ...rest }: ChoiceProps) => {
    const input = (
      <input
        type={type}
        role={block === 'switch' ? 'switch' : undefined}
        className={cx(block, hue && `${block}--${hue}`, sizeClass(block, size), className)}
        aria-invalid={invalid || undefined}
        ref={indeterminate !== undefined ? (el) => void (el && (el.indeterminate = indeterminate)) : undefined}
        {...rest}
      />
    );
    if (!label) return input;
    return (
      <label className="check">
        {input}
        <span>
          {label}
          {hint && <span className="check__hint">{hint}</span>}
        </span>
      </label>
    );
  };
  Choice.displayName = block;
  return Choice;
}

export const Checkbox = choice('checkbox', 'checkbox');
export const Radio = choice('radio', 'radio');
export const Switch = choice('switch', 'checkbox');

// -------------------------------------------------------------- segmented

export interface SegmentedProps<T extends string> {
  options: Array<{ value: T; label: ReactNode; disabled?: boolean }>;
  value: T;
  onChange: (value: T) => void;
  name?: string;
  hue?: Hue;
  size?: 'sm' | 'base';
  disabled?: boolean;
  'aria-label'?: string;
}

/** Radio buttons as a row of pill tabs. */
export function Segmented<T extends string>({
  options,
  value,
  onChange,
  name,
  hue,
  size,
  disabled,
  ...aria
}: SegmentedProps<T>) {
  const fallbackName = useId();
  return (
    <div
      role="radiogroup"
      className={cx('segmented', hue && `segmented--${hue}`, sizeClass('segmented', size))}
      aria-disabled={disabled || undefined}
      {...aria}
    >
      {options.map((option) => (
        <label key={option.value} className="segmented__option">
          <input
            type="radio"
            className="segmented__input"
            name={name ?? fallbackName}
            value={option.value}
            checked={option.value === value}
            disabled={disabled || option.disabled}
            onChange={() => onChange(option.value)}
          />
          {option.label}
        </label>
      ))}
    </div>
  );
}

// ------------------------------------------------------------------ range

export interface RangeProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'value'> {
  value: number;
  min?: number;
  max?: number;
  hue?: Hue;
}

/** Slider with the filled part coloured (sets --range-fill from the value). */
export function Range({ value, min = 0, max = 100, hue, className, style, ...rest }: RangeProps) {
  const fill = ((value - min) / (max - min)) * 100;
  return (
    <input
      type="range"
      className={cx('range', hue && `range--${hue}`, className)}
      value={value}
      min={min}
      max={max}
      style={{ ...style, ['--range-fill' as string]: `${fill}%` }}
      {...rest}
    />
  );
}
